import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash, PencilSimple, Download } from "@phosphor-icons/react";
import { Modal, Field } from "./Suppliers";
import { toast } from "sonner";

const CATEGORIES = ["Clamp", "Rail", "Bracket", "Strap", "Bolt", "Nut", "Spacer", "Pin", "Rivet", "Pallet", "Corrugated Lid", "Corrugated Wall", "Consumable", "Other"];
const EMPTY = {
  part_number: "",
  name: "",
  category: "Rail",
  unit: "pcs",
  description: "",
  customer: "",
  is_assembled: false,
  is_shippable: false,
  is_internal: false,
  is_consumable: false,
  approved_supplier_ids: [],
  bom: [],
  assemblies_per_box: null,
  min_stock_threshold: null,
};

export default function Parts() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [customerFilter, setCustomerFilter] = useState("All");

  const customerOptions = useMemo(() => {
    const s = new Set();
    items.forEach((p) => { if (p.customer) s.add(p.customer); });
    return ["All", ...Array.from(s).sort()];
  }, [items]);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/parts"), api.get("/suppliers")]);
    setItems(p.data);
    setSuppliers(s.data);
  };
  useEffect(() => { load(); }, []);

  const possibleSubParts = useMemo(
    // Allow components AND other assemblies (sub-assemblies), but exclude self
    () => items.filter((p) => p.id !== editId),
    [items, editId]
  );

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.is_assembled) payload.bom = [];
      // Cleanse bom (remove empties)
      payload.bom = (payload.bom || []).filter((b) => b.sub_part_id && b.qty_per_assembly > 0);
      if (editId) await api.put(`/parts/${editId}`, payload);
      else await api.post("/parts", payload);
      toast.success("Part saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this part?")) return;
    try { await api.delete(`/parts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const seedDefaults = async () => {
    if (!window.confirm("Seed the 11 default part numbers? Existing ones will be skipped.")) return;
    try {
      const { data } = await api.post("/parts/seed-defaults");
      toast.success(`Created ${data.created.length}, skipped ${data.skipped.length}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((p) => {
    const catOk = filter === "All" || p.category === filter;
    const isShip = p.is_assembled && p.is_shippable;
    const isSub = p.is_assembled && !p.is_shippable;
    const typeOk = typeFilter === "All"
      || (typeFilter === "Shippable" && isShip)
      || (typeFilter === "Sub-Assembly" && isSub)
      || (typeFilter === "Component" && !p.is_assembled && !p.is_consumable)
      || (typeFilter === "Consumable" && p.is_consumable);
    const custOk = customerFilter === "All" || (p.customer || "") === customerFilter;
    return catOk && typeOk && custOk;
  });

  return (
    <div className="space-y-6" data-testid="parts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Parts Master</h1>
          <p className="text-xs text-zinc-600 mt-1">Components & assemblies · approved suppliers · BoM</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button onClick={seedDefaults} className="brut-btn px-3 py-2 text-xs inline-flex items-center gap-2" data-testid="seed-parts-btn">
              <Download size={14} weight="bold" /> Import Defaults
            </button>
          )}
          {canEdit && (
            <button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="add-part-btn">
              <Plus size={14} weight="bold" /> Add Part
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["All", "Shippable", "Sub-Assembly", "Component", "Consumable"].map((t) => (
          <button key={t} onClick={() => setTypeFilter(t)} data-testid={`type-filter-${t.toLowerCase()}`} className={`brut-tag ${typeFilter === t ? "brut-tag-dark" : "brut-tag-white"}`}>{t}</button>
        ))}
        <span className="brut-tag brut-tag-white opacity-30">|</span>
        {["All", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c.toLowerCase()}`} className={`brut-tag ${filter === c ? "brut-tag-dark" : "brut-tag-white"}`}>{c}</button>
        ))}
      </div>

      {customerOptions.length > 1 && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="customer-filter-bar">
          <span className="brut-label">Customer:</span>
          {customerOptions.map((c) => (
            <button key={c} onClick={() => setCustomerFilter(c)} data-testid={`customer-filter-${c}`} className={`brut-tag ${customerFilter === c ? "brut-tag-yellow" : "brut-tag-white"}`}>
              {c || "—"}
            </button>
          ))}
        </div>
      )}

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="parts-table">
          <thead><tr><th>Part #</th><th>Name</th><th>Category</th><th>Type</th><th>Customer</th><th>Approved Suppliers</th><th>BoM</th><th></th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-zinc-500">No parts.</td></tr>}
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`part-row-${p.part_number}`}>
                <td className="font-bold">{p.part_number}</td>
                <td className="max-w-md">{p.name}</td>
                <td><span className="brut-tag brut-tag-yellow">{p.category}</span></td>
                <td>
                  {p.is_consumable
                    ? <span className="brut-tag brut-tag-yellow">Consumable</span>
                    : p.is_assembled
                    ? (p.is_shippable
                        ? <span className="brut-tag brut-tag-green">Shippable</span>
                        : <span className="brut-tag brut-tag-dark">Sub-Assembly</span>)
                    : <span className="brut-tag brut-tag-white">Component</span>}
                  {p.is_internal && <span className="brut-tag brut-tag-yellow ml-1">Internal</span>}
                  {p.is_consumable && (
                    <div className="text-[10px] font-mono-ui mt-1 text-zinc-700" data-testid={`stock-${p.part_number}`}>
                      Stock: <b>{p.stock_on_hand ?? "—"}</b> {p.unit || "pcs"}
                      {p.stock_updated_at && <span className="text-zinc-500"> · {new Date(p.stock_updated_at).toLocaleDateString()}</span>}
                    </div>
                  )}
                </td>
                <td className="text-xs">{p.customer || "—"}</td>
                <td className="text-xs">
                  {(p.approved_supplier_ids || []).length === 0 ? (
                    <span className="text-zinc-500">—</span>
                  ) : (
                    (p.approved_supplier_ids || []).map((sid) => {
                      const s = suppliers.find((x) => x.id === sid);
                      return s ? <span key={sid} className="brut-tag brut-tag-white mr-1 mb-1 inline-block">{s.code}</span> : null;
                    })
                  )}
                </td>
                <td className="font-mono-ui text-xs">{(p.bom || []).length} items</td>
                <td className="text-right whitespace-nowrap">
                  {canEdit && p.is_consumable && (
                    <button
                      onClick={async () => {
                        const cur = p.stock_on_hand ?? 0;
                        const val = window.prompt(`Update ${p.part_number} — current stock: ${cur} ${p.unit || "pcs"}\n\nEnter new quantity on hand:`, cur);
                        if (val === null || val === "") return;
                        const q = parseFloat(val);
                        if (isNaN(q) || q < 0) return toast.error("Invalid quantity");
                        try {
                          await api.post(`/parts/${p.id}/stock-update`, { qty: q, notes: "" });
                          toast.success(`Stock updated → ${q}`);
                          load();
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className="brut-tag brut-tag-blue mr-1"
                      title="Update stock on hand"
                      data-testid={`update-stock-${p.part_number}`}
                    >
                      Stock+
                    </button>
                  )}
                  {canEdit && (
                    <button onClick={() => { setForm({ ...EMPTY, ...p, approved_supplier_ids: p.approved_supplier_ids || [], bom: p.bom || [] }); setEditId(p.id); setOpen(true); }} className="brut-tag brut-tag-white mr-1" data-testid={`edit-part-${p.part_number}`}>
                      <PencilSimple size={10} weight="bold" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(p.id)} className="brut-tag brut-tag-red" data-testid={`delete-part-${p.part_number}`}>
                      <Trash size={10} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <Modal title={editId ? "Edit Part" : "New Part"} onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-3" data-testid="part-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Part Number" required value={form.part_number} onChange={(v) => setForm({ ...form, part_number: v })} testid="part-number" />
              <div>
                <label className="brut-label">Category *</label>
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="brut-input mt-1" data-testid="part-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="part-name" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Customer" value={form.customer || ""} onChange={(v) => setForm({ ...form, customer: v })} testid="part-customer" placeholder="e.g. Nissan, GM" />
              <Field label="Unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} testid="part-unit" />
            </div>
            <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="part-description" />

            <div className="brut-border p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_assembled} onChange={(e) => setForm({ ...form, is_assembled: e.target.checked, is_shippable: e.target.checked ? form.is_shippable : false })} className="w-4 h-4" data-testid="part-is-assembled" />
                <span className="brut-label">Assembled (built in-house from a BoM)</span>
              </label>
              {form.is_assembled && (
                <label className="flex items-center gap-2 cursor-pointer pl-6 border-l-4 border-[#60a5fa]">
                  <input type="checkbox" checked={form.is_shippable} onChange={(e) => setForm({ ...form, is_shippable: e.target.checked })} className="w-4 h-4" data-testid="part-is-shippable" />
                  <span className="brut-label">Shippable (final goods · can be packed into boxes)</span>
                </label>
              )}
              {form.is_assembled && !form.is_shippable && (
                <p className="text-[10px] text-zinc-600 pl-6">↳ This is a <b>SUB-ASSEMBLY</b>. Produced via Production Runs and consumed in other assemblies — but cannot be shipped on its own.</p>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_internal} onChange={(e) => setForm({ ...form, is_internal: e.target.checked })} className="w-4 h-4" data-testid="part-is-internal" />
                <span className="brut-label">Can be manufactured internally</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_consumable || false} onChange={(e) => setForm({ ...form, is_consumable: e.target.checked })} className="w-4 h-4" data-testid="part-is-consumable" />
                <span className="brut-label">Consumable (pallets, corrugated, straps — weekly stock updates)</span>
              </label>
            </div>

            {!form.is_assembled && (
              <div>
                <label className="brut-label">Approved Suppliers</label>
                <div className="brut-border max-h-40 overflow-y-auto p-2 mt-1">
                  {suppliers.length === 0 && <div className="text-xs text-zinc-500">No suppliers yet. Create one first.</div>}
                  {suppliers.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={form.approved_supplier_ids.includes(s.id)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...form.approved_supplier_ids, s.id]
                            : form.approved_supplier_ids.filter((x) => x !== s.id);
                          setForm({ ...form, approved_supplier_ids: next });
                        }}
                        className="w-4 h-4"
                        data-testid={`approved-supplier-${s.code}`}
                      />
                      <span className="font-bold">{s.code}</span> · {s.name}
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1">Tick at least one approved supplier (or mark Internal).</p>
              </div>
            )}

            {form.is_assembled && form.is_shippable && (
              <div className="brut-border p-3 bg-green-50">
                <label className="brut-label">Assemblies per Box *</label>
                <input
                  type="number"
                  min="1"
                  value={form.assemblies_per_box ?? ""}
                  onChange={(e) => setForm({ ...form, assemblies_per_box: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                  className="brut-input mt-1"
                  placeholder="e.g. 170"
                  data-testid="part-assemblies-per-box"
                />
                <p className="text-[10px] text-zinc-600 mt-1">Used when starting an Assembly Run — target units must be a multiple of this number.</p>
              </div>
            )}

            {form.is_assembled && !form.is_shippable && (
              <div className="brut-border p-3 bg-[#eff6ff]">
                <label className="brut-label">Minimum Stock Threshold</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.min_stock_threshold ?? ""}
                  onChange={(e) => setForm({ ...form, min_stock_threshold: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  className="brut-input mt-1"
                  placeholder="e.g. 50"
                  data-testid="part-min-threshold"
                />
                <p className="text-[10px] text-zinc-600 mt-1">Dashboard alerts you when on-hand falls below this.</p>
              </div>
            )}

            {form.is_assembled && (
              <BomEditor
                bom={form.bom}
                subParts={possibleSubParts}
                onChange={(bom) => setForm({ ...form, bom })}
              />
            )}

            <button type="submit" className="brut-btn brut-btn-primary px-4 py-2 mt-2" data-testid="save-part-btn">Save Part</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function BomEditor({ bom, subParts, onChange }) {
  const add = () => onChange([...bom, { sub_part_id: subParts[0]?.id || "", qty_per_assembly: 1 }]);
  const update = (i, key, val) => onChange(bom.map((b, idx) => (idx === i ? { ...b, [key]: val } : b)));
  const remove = (i) => onChange(bom.filter((_, idx) => idx !== i));

  return (
    <div className="brut-border p-3 space-y-2 bg-[#eff6ff]">
      <div className="flex justify-between items-center">
        <div>
          <div className="brut-label">Bill of Materials</div>
          <p className="text-[10px] text-zinc-600">Sub-parts consumed for ONE fully assembled unit.</p>
        </div>
        <button type="button" onClick={add} disabled={subParts.length === 0} className="brut-tag brut-tag-dark inline-flex items-center gap-1 disabled:opacity-50" data-testid="add-bom-btn">
          <Plus size={10} weight="bold" /> Sub-Part
        </button>
      </div>
      {bom.length === 0 ? (
        <div className="text-xs text-zinc-500 text-center py-2">No sub-parts added yet.</div>
      ) : (
        <div className="space-y-2">
          {bom.map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`bom-row-${i}`}>
              <div className="col-span-8">
                <label className="brut-label">Sub-Part</label>
                <select value={b.sub_part_id} onChange={(e) => update(i, "sub_part_id", e.target.value)} className="brut-input mt-1" data-testid={`bom-part-${i}`}>
                  <option value="">— Select —</option>
                  {subParts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.part_number} · {p.name}{p.is_assembled ? "  (Sub-Assembly)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <label className="brut-label">Qty / Assembly</label>
                <input type="number" min="0" step="any" value={b.qty_per_assembly} onChange={(e) => update(i, "qty_per_assembly", parseFloat(e.target.value) || 0)} className="brut-input mt-1" data-testid={`bom-qty-${i}`} />
              </div>
              <div className="col-span-1 flex justify-end">
                <button type="button" onClick={() => remove(i)} className="brut-tag brut-tag-red" data-testid={`bom-remove-${i}`}>
                  <Trash size={10} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
